<template>
  <div class="dashboard-header">
    <div class="dashboard-header-row">
      <div class="date-controls">
        <div class="date-selector">
          <button
            v-for="option in dateOptions"
            :key="option.value"
            class="date-btn"
            :class="{'active': selectedDate === option.value}"
            @click="updateDateRange(option.value)"
          >
            {{ option.label }}
          </button>
          <button
            class="date-btn"
            :class="{'active': selectedDate === 'custom'}"
            @click="toggleDatePicker()"
          >
            Custom Range
          </button>
        </div>
      </div>
      <div class="refresh-info">
        <span>Updated: {{ lastUpdatedTime }}</span>
        <div class="refresh-controls">
          <button @click="refresh()" class="refresh-btn">
            <i class="material-icons">refresh</i>
            Refresh
          </button>
          <button @click="toggleAutoRefresh()" class="auto-refresh-toggle" :class="{'active': autoRefreshEnabled}">
            <i class="material-icons">{{ autoRefreshEnabled ? 'sync' : 'sync_disabled' }}</i>
            <span class="toggle-text">{{ autoRefreshEnabled ? 'Auto On' : 'Auto Off' }}</span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="showDatePicker" class="custom-date-picker">
      <div class="date-inputs">
        <div class="date-input-group">
          <label>Start Date</label>
          <input type="date" v-model="customDates.start" :max="today">
        </div>
        <div class="date-input-group">
          <label>End Date</label>
          <input type="date" v-model="customDates.end" :max="today">
        </div>
      </div>
      <div class="date-picker-actions">
        <button class="btn-secondary" @click="cancelCustomDate">Cancel</button>
        <button class="btn-primary" @click="applyCustomDate" :disabled="!isValidDateRange">Apply</button>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from "date-fns";
import { businessToday } from "../../utils/business-date";

export default {
  name: "DashboardHeader",
  props: {
    selectedDate: {
      type: String,
      default: 'last7days'
    },
    lastUpdatedTime: {
      type: String,
      default: () => format(new Date(), 'h:mm a')
    },
    autoRefreshEnabled: {
      type: Boolean,
      default: false
    },
    customDateRange: {
      type: Object,
      default: () => ({
        start: '',
        end: ''
      })
    }
  },
  data() {
    return {
      dateOptions: [
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'last7days', label: 'Last 7 days' },
        { value: 'last30days', label: 'Last 30 days' },
        { value: 'last90days', label: 'Last 90 days' },
        { value: 'lastYear', label: 'Last Year' },
        { value: 'thisMonth', label: 'This Month' },
        { value: 'lastMonth', label: 'Last Month' }
      ],
      showDatePicker: false,
      customDates: {
        start: this.customDateRange.start,
        end: this.customDateRange.end
      },
      today: businessToday()
    };
  },
  computed: {
    isValidDateRange() {
      return this.customDates.start && this.customDates.end;
    }
  },
  methods: {
    updateDateRange(range) {
      this.$emit('update-date', range);
    },
    toggleDatePicker() {
      this.showDatePicker = !this.showDatePicker;
      if (this.showDatePicker) {
        this.customDates = {
          start: this.customDateRange.start,
          end: this.customDateRange.end
        };
      }
    },
    cancelCustomDate() {
      this.showDatePicker = false;
    },
    applyCustomDate() {
      if (!this.isValidDateRange) return;

      this.$emit('apply-custom-date', {
        start: this.customDates.start,
        end: this.customDates.end
      });

      this.showDatePicker = false;
    },
    refresh() {
      this.$emit('refresh');
    },
    toggleAutoRefresh() {
      this.$emit('toggle-auto-refresh');
    }
  }
};
</script>

<style scoped lang="scss">
@use "sass:color";
@import "@/assets/variables.scss";

.dashboard-header {
  margin-bottom: 16px;

  .dashboard-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 12px;
    padding: 10px 12px;
    box-shadow: none; /* Keep flat to align with header elevation */
    backdrop-filter: none; /* Avoid haze */
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-1px);
    }

    .dark & {
      background: $darkCardBg;
      border-color: $darkBorderColor;
      box-shadow: none;

      &:hover {

      }
    }
  }

  .date-controls {
    flex: 1 1 auto;
    min-width: 0;

    .date-selector {
      display: flex;
      align-items: center;
      gap: 6px;
      max-width: 100%;
      overflow-x: auto;
      padding-bottom: 2px;
      scrollbar-width: thin;

      .date-btn {
        flex: 0 0 auto;
        background: transparent;
        color: $textColor;
        border: 1px solid $lightBorderColor;
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.25;
        white-space: nowrap;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s;
        }

        &:hover {
          background: $lightHoverColor;
          border-color: $primaryColor;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

          &::before {
            left: 100%;
          }
        }

        &.active {
          background: linear-gradient(135deg, $primaryColor 0%, color.adjust($primaryColor, $lightness: -5%) 100%);
          color: white;
          border-color: $primaryColor;
          box-shadow: 0 2px 8px rgba($primaryColor, 0.3);

          &::before {
            display: none;
          }
        }

        .dark & {
          color: $darkTextColor;
          border-color: $darkBorderColor;

          &:hover {
            background: color.adjust($darkCardBg, $lightness: 5%);
            border-color: $secondaryColorDark;
          }

          &.active {
            background: linear-gradient(135deg, $primaryColorDark 0%, color.adjust($primaryColorDark, $lightness: -5%) 100%);
            border-color: $primaryColorDark;
            box-shadow: 0 2px 8px rgba($primaryColorDark, 0.3);
          }
        }
      }
    }
  }

  .refresh-info {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: $textColor;
    opacity: 0.8;
    font-weight: 500;

    .dark & {
      color: $darkTextColor;
    }

    .refresh-controls {
      display: flex;
      gap: 6px;

      .refresh-btn, .auto-refresh-toggle {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 4px;
        background: transparent;
        color: $textColor;
        border: 1px solid $lightBorderColor;
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;

        i {
          font-size: 14px;
          transition: transform 0.3s ease;
        }

        &:hover {
          background: $lightHoverColor;
          border-color: $primaryColor;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

          i {
            transform: rotate(180deg);
          }
        }

        .dark & {
          color: $darkTextColor;
          border-color: $darkBorderColor;

          &:hover {
            background: color.adjust($darkCardBg, $lightness: 5%);
            border-color: $secondaryColorDark;
          }
        }
      }

      .auto-refresh-toggle {
        &.active {
          background: linear-gradient(135deg, $successColor 0%, color.adjust($successColor, $lightness: -5%) 100%);
          color: white;
          border-color: $successColor;
          box-shadow: 0 2px 8px rgba($successColor, 0.3);

          &:hover {
            box-shadow: 0 4px 12px rgba($successColor, 0.4);

            i {
              transform: scale(1.1);
            }
          }
        }
      }
    }
  }

  .custom-date-picker {
    background: linear-gradient(135deg, $lightCardBg 0%, color.adjust($lightCardBg, $lightness: 1%) 100%);
    border: 1px solid $lightBorderColor;
    border-radius: 12px;
    padding: 18px;
    margin-top: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    backdrop-filter: blur(10px);
    animation: slideDown 0.3s ease-out;

    .dark & {
      background: linear-gradient(135deg, $darkCardBg 0%, color.adjust($darkCardBg, $lightness: 2%) 100%);
      border-color: $darkBorderColor;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }

    .date-inputs {
      display: flex;
      gap: 18px;
      margin-bottom: 18px;

      @media (max-width: 576px) {
        flex-direction: column;
        gap: 12px;
      }

      .date-input-group {
        flex: 1;

        label {
          display: block;
          margin-bottom: 6px;
          font-size: 13px;
          font-weight: 600;
          color: $primaryColor;

          .dark & {
            color: $secondaryColorDark;
          }
        }

        input[type="date"] {
          width: 100%;
          padding: 10px 14px;
          border: 1px solid $lightBorderColor;
          border-radius: 6px;
          background: $lightCardBg;
          color: $textColor;
          font-size: 13px;
          transition: all 0.3s ease;

          &:focus {
            outline: none;
            border-color: $primaryColor;
            box-shadow: 0 0 0 3px rgba($primaryColor, 0.1);
          }

          .dark & {
            background: $darkInputBg;
            border-color: $darkBorderColor;
            color: $darkTextColor;

            &:focus {
              border-color: $secondaryColorDark;
              box-shadow: 0 0 0 3px rgba($secondaryColorDark, 0.1);
            }
          }
        }
      }
    }

    .date-picker-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;

      .btn-secondary {
        background: transparent;
        color: $textColor;
        border: 1px solid $lightBorderColor;
        border-radius: 6px;
        padding: 8px 16px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: $lightHoverColor;
          border-color: $primaryColor;
        }

        .dark & {
          color: $darkTextColor;
          border-color: $darkBorderColor;

          &:hover {
            background: color.adjust($darkCardBg, $lightness: 5%);
            border-color: $secondaryColorDark;
          }
        }
      }

      .btn-primary {
        background: linear-gradient(135deg, $primaryColor 0%, color.adjust($primaryColor, $lightness: -5%) 100%);
        color: white;
        border: none;
        border-radius: 6px;
        padding: 8px 16px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 2px 8px rgba($primaryColor, 0.3);

        &:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba($primaryColor, 0.4);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          box-shadow: none;
        }

        .dark & {
          background: linear-gradient(135deg, $primaryColorDark 0%, color.adjust($primaryColorDark, $lightness: -5%) 100%);
          box-shadow: 0 2px 8px rgba($primaryColorDark, 0.3);

          &:hover:not(:disabled) {
            box-shadow: 0 4px 12px rgba($primaryColorDark, 0.4);
          }
        }
      }
    }
  }
}

@media (max-width: 1500px) {
  .dashboard-header {
    .dashboard-header-row {
      flex-wrap: wrap;
    }

    .date-controls {
      flex-basis: 100%;
      order: 1;
    }

    .refresh-info {
      order: 2;
      width: 100%;
      justify-content: space-between;
    }
  }
}

@media (max-width: 576px) {
  .dashboard-header {
    .dashboard-header-row {
      padding: 10px;
    }

    .date-controls .date-selector {
      gap: 5px;
      margin-right: -2px;
      padding-bottom: 4px;
    }

    .date-controls .date-selector .date-btn {
      padding: 8px 10px;
      min-height: 38px;
    }

    .refresh-info {
      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
    }

    .refresh-info .refresh-controls {
      width: 100%;
    }

    .refresh-info .refresh-controls .refresh-btn,
    .refresh-info .refresh-controls .auto-refresh-toggle {
      flex: 1 1 0;
      justify-content: center;
    }
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
